import { getBezierPath, Position, useReactFlow } from "reactflow";
import { Direction } from "containers/Resources/screens/ResourcesSingle/components/Visualization/visualization.service";

// https://reactflow.dev/examples/edges/simple-floating-edges

// returns the position (top, right, bottom or left) of nodeA's handle facing nodeB.
//
// The anchor axis follows the LAYOUT DIRECTION (item 9): in a horizontal (LR)
// layout edges always enter/exit on the left/right sides; in a vertical (TB)
// layout always on the top/bottom sides. This keeps anchors consistent with the
// dagre flow direction instead of flipping to the perpendicular axis whenever a
// child happens to sit far above/below (or beside) its parent. When no layout
// direction is supplied we fall back to the geometry-based heuristic.
function getParams(nodeA, nodeB, layoutDirection) {
    const centerA = getNodeCenter(nodeA);
    const centerB = getNodeCenter(nodeB);

    let position;

    if (layoutDirection === Direction.Horizontal) {
        // horizontal layout: anchor on the left/right side facing the other node
        position = centerA.x > centerB.x ? Position.Left : Position.Right;
    } else if (layoutDirection === Direction.Vertical) {
        // vertical layout: anchor on the top/bottom side facing the other node
        position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
    } else {
        // no direction supplied: pick the axis with the larger centre-to-centre gap
        const horizontalDiff = Math.abs(centerA.x - centerB.x);
        const verticalDiff = Math.abs(centerA.y - centerB.y);
        if (horizontalDiff > verticalDiff) {
            position = centerA.x > centerB.x ? Position.Left : Position.Right;
        } else {
            position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
        }
    }

    const [x, y] = getHandleCoordsByPosition(nodeA, position);
    return [x, y, position];
}

function getHandleCoordsByPosition(node, handlePosition) {
    // all handles are from type source, that's why we use handleBounds.source here
    const symbolProp = Object.getOwnPropertySymbols(node);
    const handle = node[symbolProp[0]].handleBounds.source.find(
        (h) => h.position === handlePosition
    );

    let offsetX = handle.width / 2;
    let offsetY = handle.height / 2;

    const x = node.positionAbsolute.x + handle.x + offsetX;
    const y = node.positionAbsolute.y + handle.y + offsetY;

    return [x, y];
}

function getNodeCenter(node) {
    return {
        x: node.positionAbsolute.x + node.width / 2,
        y: node.positionAbsolute.y + node.height / 2,
    };
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source, target, layoutDirection?) {
    const [sx, sy, sourcePos] = getParams(source, target, layoutDirection);
    const [tx, ty, targetPos] = getParams(target, source, layoutDirection);

    return {
        sx,
        sy,
        tx,
        ty,
        sourcePos,
        targetPos,
    };
}

export const FloatingEdge = ({
    id,
    source,
    target,
    style,
    data: { onMouseEnter, layoutDirection },
    markerStart,
    markerEnd,
}: any) => {
    const { getNode } = useReactFlow();
    const sourceNode = getNode(source);
    const targetNode = getNode(target);

    const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
        sourceNode,
        targetNode,
        layoutDirection
    );

    const [edgePath] = getBezierPath({
        sourceX: sx,
        sourceY: sy,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        targetX: tx,
        targetY: ty,
    });
    return (
        <>
            <path
                id={id}
                style={style}
                className="react-flow__edge-path"
                d={edgePath}
                onMouseEnter={onMouseEnter}
                markerStart={markerStart}
                markerEnd={markerEnd}
            />
        </>
    );
};
